import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  Avatar,
  IconButton,
  ListItem as _ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import CloudDownload from '@material-ui/icons/CloudDownload';
import Delete from '@material-ui/icons/Delete';
import FontDownload from '@material-ui/icons/FontDownload';
import Image from '@material-ui/icons/Image';
import InsertDriveFile from '@material-ui/icons/InsertDriveFile';
import MoreVert from '@material-ui/icons/MoreVert';
import Videocam from '@material-ui/icons/Videocam';
import {
  ClassAttributes,
  createElement,
  createRef,
  Fragment,
  MouseEvent,
  MouseEventHandler,
  PureComponent,
  RefObject,
} from 'react';
import { FormattedMessage } from 'react-intl';

import mq from '../../../styleUtility/mediaquery';
import FileSize from '../../primitive/FileSize';

export type Props = ClassAttributes<HTMLElement> & {
  file: FileReference;
  isVisibleSecondary?: boolean;
  onClickDeleteButton?: MouseEventHandler<HTMLElement>;
  className?: string;
  isPC?: boolean;
};

type State = {
  isVisibleSecondary: boolean;
  isMenuOpen: boolean;
};

class FileListItem extends PureComponent<Props, State> {
  private readonly menuButton: RefObject<HTMLElement>;

  public state: State = {
    isVisibleSecondary: this.props.isVisibleSecondary !== undefined ? this.props.isVisibleSecondary : true,
    isMenuOpen: false,
  };

  public render() {
    let Icon = InsertDriveFile;

    if (this.props.file.mimetype.startsWith('image/')) {
      Icon = Image;
    } else if (this.props.file.mimetype.startsWith('video/')) {
      Icon = Videocam;
    } else if (this.props.file.mimetype.startsWith('font/')) {
      Icon = FontDownload;
    }

    const OriginalName = this.props.onClickDeleteButton ? OriginalNameWithButton : OriginalNameWithoutButton;
    return (
      <>
        <ListItem button className={this.props.className}>
          <ListItemIcon>
            <Avatar>
              <Icon />
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={
              <OriginalName variant="subtitle1" noWrap>
                {this.props.file.originalName}
              </OriginalName>
            }
            secondary={
              this.state.isVisibleSecondary ? (
                <TypographyText variant="body2" color="textSecondary" noWrap>
                  <FileSize fileSize={this.props.file.size}>
                    {this.props.isPC
                      ? (fileSize) => `${fileSize} - ${this.props.file.createdAt.toFormat('yyyy-MM-dd HH:mm:ss')}`
                      : (fileSize) => `${fileSize}\n${this.props.file.createdAt.toFormat('yyyy-MM-dd HH:mm:ss')}`}
                  </FileSize>
                </TypographyText>
              ) : null
            }
            disableTypography
          />
          <ListItemSecondaryAction>
            <IconButton component="a" href={this.props.file.url} target="_blank">
              <CloudDownload />
            </IconButton>

            {this.props.onClickDeleteButton ? (
              <IconButton onClick={this.onClickMenuButton} buttonRef={this.menuButton}>
                <MoreVert />
              </IconButton>
            ) : null}
          </ListItemSecondaryAction>
        </ListItem>

        {this.props.onClickDeleteButton ? (
          <Menu anchorEl={this.menuButton.current!} open={this.state.isMenuOpen} onClose={this.onRequestCloseMenu}>
            <MenuItem onClick={this.onClickDeleteButton}>
              <ListItemIcon>
                <Delete />
              </ListItemIcon>

              <FormattedMessage id="file_list.file_list_item.delete">
                {(label: string) => <ListItemText inset primary={label} />}
              </FormattedMessage>
            </MenuItem>
          </Menu>
        ) : null}
      </>
    );
  }

  private onClickMenuButton = () => this.setState({ isMenuOpen: true });

  private onRequestCloseMenu = () => this.setState({ isMenuOpen: false });

  private onClickDeleteButton = (e: MouseEvent<HTMLElement>) => {
    this.onRequestCloseMenu();

    this.props.onClickDeleteButton!(e);
  };

  public constructor(props: Props) {
    super(props);

    this.menuButton = createRef();
  }
}

const ListItem = styled(_ListItem)({
  [mq('sp')]: {
    paddingTop: 0,
    paddingBottom: 0,
  },
});

const OriginalNameWithoutButton = styled(Typography)({
  paddingRight: 0,
});

const OriginalNameWithButton = styled(Typography)({
  paddingRight: 48,
});

const TypographyText = styled(Typography)({
  [mq('sp')]: {
    width: '90px',
    whiteSpace: 'pre',
  },
});

export default FileListItem;
